// Phase 10 — Admin Certificate Moderation
import React, { useEffect, useState, useCallback } from "react";
import {
  Award, Search, ShieldCheck, ShieldOff, RefreshCw, Loader2,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

export default function AdminCertificateModeration() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/certificates", { params: { q, role } });
      setRows(data.certificates || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, [q, role]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (cert, revoke) => {
    const reason = revoke ? (window.prompt("Reason for revocation:") || "Manual revocation") : "";
    setBusy(cert.certification_id);
    try {
      await api.patch(`/admin/certificates/${cert.certification_id}`, revoke ? { revoke: true, reason } : { restore: true });
      await load();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="admin-cert-moderation">
      <header className="flex items-end justify-between">
        <div>
          <div className="eyebrow">Admin · Training</div>
          <h1 className="font-anton text-3xl uppercase">Certificate Moderation</h1>
        </div>
        <button onClick={load} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="cert-mod-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </header>

      {err && <div className="text-rose-300 text-sm" data-testid="cert-mod-error">{err}</div>}

      <div className="flex flex-wrap gap-2">
        <div className="relative">
          <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-lifted-dim" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cert ID or name"
                 className="bg-black/40 border border-white/10 rounded-md pl-7 pr-2 py-1.5 text-xs"
                 data-testid="cert-mod-search" />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)}
                className="bg-black/40 border border-white/10 rounded-md px-2 text-xs"
                data-testid="cert-mod-role">
          <option value="">All roles</option>
          <option value="affiliate">Affiliate</option>
          <option value="driver">Driver</option>
          <option value="partner">Partner</option>
          <option value="franchisee">Franchise</option>
        </select>
      </div>

      <div className="glass-panel overflow-hidden">
        {!rows ? (
          <div className="p-4 text-xs text-lifted-dim">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-xs text-lifted-dim">No certificates match the filter.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
              <tr className="border-b border-white/5">
                <th className="px-3 py-2 text-left">Cert ID</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Role</th>
                <th className="px-3 py-2 text-left">Score</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.certification_id || c.id} className="border-b border-white/5" data-testid={`cert-row-${c.certification_id}`}>
                  <td className="px-3 py-2 font-mono">{c.certification_id}</td>
                  <td className="px-3 py-2">{c.name || "—"}</td>
                  <td className="px-3 py-2 capitalize">{c.role}</td>
                  <td className="px-3 py-2">{c.score}/{c.total}</td>
                  <td className="px-3 py-2">
                    {c.revoked ? (
                      <span className="text-rose-300 inline-flex items-center gap-1" data-testid={`cert-${c.certification_id}-revoked`}>
                        <ShieldOff className="h-3 w-3" /> Revoked
                      </span>
                    ) : (
                      <span className="text-lifted-green inline-flex items-center gap-1" data-testid={`cert-${c.certification_id}-active`}>
                        <ShieldCheck className="h-3 w-3" /> Verified
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {c.revoked ? (
                      <button onClick={() => toggle(c, false)} disabled={busy === c.certification_id}
                              className="btn-ghost !py-1 !px-2 text-[0.65rem] inline-flex items-center gap-1"
                              data-testid={`cert-restore-${c.certification_id}`}>
                        {busy === c.certification_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Award className="h-3 w-3" />}
                        Restore
                      </button>
                    ) : (
                      <button onClick={() => toggle(c, true)} disabled={busy === c.certification_id}
                              className="btn-ghost !py-1 !px-2 text-[0.65rem] inline-flex items-center gap-1 hover:text-rose-300"
                              data-testid={`cert-revoke-${c.certification_id}`}>
                        {busy === c.certification_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldOff className="h-3 w-3" />}
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
